/**
 * AI Governance · Approvals — full implementation against the
 * `/lighthouse/approvals*` endpoints on the EDR connector. Picks up
 * the 2026-06-06 patch: binds to `request.agent.*`, renders an
 * "orphan" badge when `agent.found === false`, and surfaces
 * `staleCleaned` in the sweep toast.
 */
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ConnectorGate } from "@/components/connectors/ConnectorGate";
import { SurfaceShell, StatTile } from "@/components/surface/SurfaceShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Plus, RefreshCw } from "lucide-react";
import {
  useApprovalsList,
  useApprovalsSummary,
  useRunApprovalSweep,
} from "@/hooks/security/useApprovals";
import {
  APPROVAL_STATES,
  APPROVAL_STATE_LABEL,
  type ApprovalRequestDTO,
  type ApprovalState,
} from "@/types/lighthouseApprovals";
import { ApprovalsTable } from "@/components/lighthouse/approvals/ApprovalsTable";
import { ApprovalDrawer } from "@/components/lighthouse/approvals/ApprovalDrawer";
import { ApproveDialog } from "@/components/lighthouse/approvals/ApproveDialog";
import { DenyOrInfoDialog } from "@/components/lighthouse/approvals/DenyOrInfoDialog";
import { RequestApprovalDialog } from "@/components/lighthouse/approvals/RequestApprovalDialog";
import { stateChipClass } from "@/components/lighthouse/approvals/approvalsUi";

const DEFAULT_STATES: ApprovalState[] = ["pending", "info_requested"];

export default function LhApprovalsPage() {
  return (
    <ConnectorGate connectorId="lighthouse">
      <Inner />
    </ConnectorGate>
  );
}

function parseStates(raw: string | null): ApprovalState[] {
  if (!raw) return DEFAULT_STATES;
  const parts = raw.split(",").filter((s): s is ApprovalState =>
    (APPROVAL_STATES as string[]).includes(s),
  );
  return parts.length ? parts : DEFAULT_STATES;
}

function Inner() {
  const [params, setParams] = useSearchParams();
  const states = parseStates(params.get("state"));
  const selectedId = params.get("id");
  const agentFilter = params.get("agentId");

  const summaryQ = useApprovalsSummary();
  const listQ = useApprovalsList(states, 200);
  const sweep = useRunApprovalSweep();

  const [requestOpen, setRequestOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState<ApprovalRequestDTO | null>(null);
  const [denyTarget, setDenyTarget] = useState<ApprovalRequestDTO | null>(null);
  const [infoTarget, setInfoTarget] = useState<ApprovalRequestDTO | null>(null);

  const counts = summaryQ.data ?? listQ.data?.counts;

  const filtered = useMemo(() => {
    const list = listQ.data?.requests ?? [];
    if (!agentFilter) return list;
    return list.filter((r) => r.agentId === agentFilter);
  }, [listQ.data, agentFilter]);

  const expiringToday = useMemo(() => {
    const now = Date.now();
    const horizon = now + 24 * 3600 * 1000;
    return (listQ.data?.requests ?? []).filter((r) => {
      const exp = r.conditions?.expires_at;
      if (!exp) return false;
      const t = new Date(exp).getTime();
      return t > now && t < horizon;
    }).length;
  }, [listQ.data]);

  const setStates = (next: ApprovalState[]) => {
    const merged = new URLSearchParams(params);
    if (
      next.length === DEFAULT_STATES.length &&
      next.every((s) => DEFAULT_STATES.includes(s))
    ) {
      merged.delete("state");
    } else {
      merged.set("state", next.join(","));
    }
    setParams(merged, { replace: true });
  };

  const setSelectedId = (id: string | null) => {
    const merged = new URLSearchParams(params);
    if (id) merged.set("id", id);
    else merged.delete("id");
    setParams(merged, { replace: true });
  };

  const toggleState = (s: ApprovalState) => {
    if (states.includes(s)) setStates(states.filter((x) => x !== s));
    else setStates([...states, s]);
  };

  const pending = counts?.pending ?? 0;
  const info = counts?.infoRequested ?? 0;
  const approved = counts?.approved ?? 0;

  const summary =
    `${pending} pending · ${info} awaiting info · ${approved} approved this month` +
    (expiringToday > 0 ? ` · ${expiringToday} expiring today` : "");

  return (
    <SurfaceShell
      overline="AI Governance · Approvals"
      title="Decide what's allowed to run"
      summary={summary}
      error={listQ.isError ? listQ.error : undefined}
      onRetry={listQ.isError ? () => listQ.refetch() : undefined}
      actions={
        <>
          <Button
            size="sm"
            variant="outline"
            disabled={sweep.isPending}
            onClick={() => sweep.mutate()}
          >
            <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", sweep.isPending && "animate-spin")} />
            Run sweep
          </Button>
          <Button size="sm" onClick={() => setRequestOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Request approval
          </Button>
        </>
      }
      kpis={
        <>
          <StatTile label="Pending" value={pending} loading={summaryQ.isLoading && listQ.isLoading} tone={pending > 0 ? "warning" : "success"} />
          <StatTile label="Awaiting info" value={info} loading={summaryQ.isLoading && listQ.isLoading} tone={info > 0 ? "info" : "default"} />
          <StatTile label="Approved" value={approved} loading={summaryQ.isLoading && listQ.isLoading} tone="success" hint="All time" />
          <StatTile label="Expiring 24h" value={expiringToday} loading={listQ.isLoading} tone={expiringToday > 0 ? "warning" : "default"} />
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {APPROVAL_STATES.map((s) => {
            const active = states.includes(s);
            const n = counts ? countOf(counts, s) : 0;
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleState(s)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                  active ? stateChipClass(s) : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                {APPROVAL_STATE_LABEL[s]}
                <Badge variant="outline" className="border-transparent bg-background/60 tabular-nums">
                  {n}
                </Badge>
              </button>
            );
          })}
        </div>

        {agentFilter && (
          <div className="text-xs text-muted-foreground">
            Filtered to agent <code className="text-foreground">{agentFilter}</code> ·{" "}
            <button
              className="underline"
              onClick={() => {
                const merged = new URLSearchParams(params);
                merged.delete("agentId");
                setParams(merged, { replace: true });
              }}
            >
              clear
            </button>
          </div>
        )}

        {listQ.isLoading ? (
          <SkeletonRows />
        ) : filtered.length === 0 ? (
          <EmptyState
            onSweep={() => sweep.mutate()}
            sweeping={sweep.isPending}
          />
        ) : (
          <ApprovalsTable
            requests={filtered}
            onRowClick={(r) => setSelectedId(r.id)}
            onApprove={setApproveTarget}
            onDeny={setDenyTarget}
            onInfo={setInfoTarget}
          />
        )}
      </div>

      <ApprovalDrawer
        id={selectedId}
        onOpenChange={(open) => !open && setSelectedId(null)}
        onApprove={setApproveTarget}
        onDeny={setDenyTarget}
        onInfo={setInfoTarget}
      />

      <ApproveDialog
        request={approveTarget}
        open={!!approveTarget}
        onOpenChange={(open) => !open && setApproveTarget(null)}
      />
      <DenyOrInfoDialog
        request={denyTarget}
        open={!!denyTarget}
        onOpenChange={(open) => !open && setDenyTarget(null)}
        mode="deny"
      />
      <DenyOrInfoDialog
        request={infoTarget}
        open={!!infoTarget}
        onOpenChange={(open) => !open && setInfoTarget(null)}
        mode="info"
      />
      <RequestApprovalDialog open={requestOpen} onOpenChange={setRequestOpen} />
    </SurfaceShell>
  );
}

function countOf(counts: { pending: number; approved: number; denied: number; infoRequested: number; expired: number }, s: ApprovalState): number {
  switch (s) {
    case "pending": return counts.pending;
    case "info_requested": return counts.infoRequested;
    case "approved": return counts.approved;
    case "denied": return counts.denied;
    case "expired": return counts.expired;
  }
}

function SkeletonRows() {
  return (
    <div className="rounded-md border border-border divide-y divide-border">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-12 bg-muted/30 animate-pulse" />
      ))}
    </div>
  );
}

function EmptyState({ onSweep, sweeping }: { onSweep: () => void; sweeping: boolean }) {
  return (
    <div className="rounded-md border border-dashed border-border p-10 text-center">
      <h3 className="text-base font-semibold text-foreground">You're caught up.</h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
        No agents are awaiting your sign-off. New pending approvals appear here when a restricted
        publisher gets a new agent, an operator manually requests one, or an approval's
        expires_at falls behind.
      </p>
      <div className="mt-4">
        <Button size="sm" variant="outline" onClick={onSweep} disabled={sweeping}>
          <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", sweeping && "animate-spin")} />
          Run sweep now
        </Button>
      </div>
    </div>
  );
}
